import React, { useState, useEffect } from 'react';
import { Plus, Clock, Pencil, Trash2, X, Check, Loader2, PoundSterling } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServiceCard = ({ service, onEdit, onDelete }) => (
  <Card className="hover:shadow-md transition-shadow">
    <CardContent className="pt-6">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900">{service.name}</h3>
          {service.description && (
            <p className="text-sm text-gray-500 mt-1">{service.description}</p>
          )}
        </div>
        <div className="flex gap-1">
          <button 
            onClick={() => onEdit(service)}
            className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
            data-testid={`edit-service-${service.id}`}
          >
            <Pencil size={16} />
          </button>
          <button 
            onClick={() => onDelete(service)}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            data-testid={`delete-service-${service.id}`}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
      
      <div className="flex items-center gap-4 pt-3 border-t border-gray-100">
        {service.default_price ? (
          <div className="flex items-center gap-1 text-emerald-600">
            <PoundSterling size={16} />
            <span className="font-semibold">£{service.default_price.toFixed(2)}</span>
          </div>
        ) : (
          <span className="text-sm text-zinc-400 italic">No default price set</span>
        )}
        <div className="flex items-center gap-1 text-gray-500">
          <Clock size={16} />
          <span>{service.default_duration || 30} mins</span>
        </div>
      </div>
    </CardContent>
  </Card>
);

const Services = () => {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    default_price: '',
    default_duration: '60',
    description: ''
  });

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/services`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setServices(data);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleAdd = () => {
    setEditingService(null);
    setFormData({
      name: '',
      default_price: '',
      default_duration: '60',
      description: ''
    });
    setShowModal(true);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      default_price: service.default_price?.toString() || '',
      default_duration: service.default_duration?.toString() || '60',
      description: service.description || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (service) => {
    if (!window.confirm(`Delete "${service.name}"? This won't affect existing jobs.`)) return;
    
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/services/${service.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setServices(services.filter(s => s.id !== service.id));
      }
    } catch (error) {
      console.error('Error deleting service:', error);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert('Please enter a service name');
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const payload = {
        name: formData.name.trim(),
        default_price: parseFloat(formData.default_price) || null,
        default_duration: parseInt(formData.default_duration) || 60,
        description: formData.description.trim() || null
      };

      const url = editingService 
        ? `${API_URL}/api/services/${editingService.id}`
        : `${API_URL}/api/services`;
      
      const response = await fetch(url, {
        method: editingService ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        if (editingService) {
          setServices(services.map(s => s.id === editingService.id ? data : s));
        } else {
          setServices([...services, data]);
        }
        setShowModal(false);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save service');
      }
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Failed to save service');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 bg-white border-b border-gray-200">
        <h1 className="text-xl font-semibold text-zinc-800">Services</h1>
        <p className="text-sm text-gray-500">Manage your service types for jobs</p>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4">
        {services.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No services yet. Add your first service!</p>
            <button
              onClick={handleAdd}
              className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
            >
              Add Service
            </button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {services.map((service) => (
              <ServiceCard 
                key={service.id} 
                service={service} 
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Add Button */}
      <button 
        onClick={handleAdd}
        className="fixed bottom-20 right-6 w-14 h-14 bg-emerald-500 rounded-full shadow-lg flex items-center justify-center text-white hover:bg-emerald-600 transition-colors"
        data-testid="add-service-btn"
      >
        <Plus size={24} />
      </button>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => !isSaving && setShowModal(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-zinc-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-zinc-900">
                {editingService ? 'Edit Service' : 'Add Service'}
              </h2>
              <button 
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
                disabled={isSaving}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Service Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Window Clean, Gutter Clearance"
                  data-testid="service-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">
                    Default Price (£)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.default_price}
                    onChange={(e) => setFormData({ ...formData, default_price: e.target.value })}
                    placeholder="20.00"
                    data-testid="service-price-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">
                    Duration (mins)
                  </label>
                  <Input
                    type="number"
                    value={formData.default_duration}
                    onChange={(e) => setFormData({ ...formData, default_duration: e.target.value })}
                    placeholder="60"
                    data-testid="service-duration-input"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Description (optional)
                </label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the service"
                  data-testid="service-description-input"
                />
              </div>
            </div>
            
            <div className="p-4 bg-zinc-50 border-t border-zinc-100 flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2 text-zinc-600 font-medium hover:text-zinc-900 transition-colors"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 py-2 bg-emerald-500 text-white font-medium rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="save-service-btn"
              >
                {isSaving ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;
